import { useState, useEffect } from "react";
import "./css/orderinfo.css";
import ReasonInputModal from "./ReasonInputModal";
import { apiGet, apiPut } from "../../../utils/apiClient";
import { formatDateTime } from "../../../utils/commonService";
import { IoPerson } from "../../../components/icons";

const STATUS_CANCEL = "취소";

const statusClass = (status) => {
  if (status === STATUS_CANCEL) return "cancel";
  if (status === "결제 완료") return "paid";
  return "etc";
};

const OrderInfo = ({ onClose, orderCode }) => {
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkedIdxList, setCheckedIdxList] = useState([]);
  const [showReasonModal, setShowReasonModal] = useState(false);

  useEffect(() => {
    const fetchOrderDetail = async () => {
      if (!orderCode) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await apiGet(`/store/order/detail/${orderCode}`);
        const data = await response.json();

        if (response.ok) {
          setOrderData(data);
        }
      } catch (error) {
        console.error("주문 상세 정보 조회 중 오류 발생:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetail();
  }, [orderCode]);

  const reloadOrderData = async () => {
    try {
      const response = await apiGet(`/store/order/detail/${orderCode}`);
      const data = await response.json();
      if (response.ok) {
        setOrderData(data);
      }
    } catch (error) {
      console.error('데이터 재조회 실패:', error);
    }
  };

  const handleUseItem = async (orderDetailIdx) => {
    if (!window.confirm('사용 처리 하시겠습니까?')) return;
    try {
      const response = await apiPut('/store/order/qr/use', { orderDetailIdxList: [orderDetailIdx] });
      if (response.ok) {
        alert('사용 처리 되었습니다.');
        await reloadOrderData();
      } else {
        const result = await response.json().catch(() => null);
        alert('처리 실패: ' + (result?.message || '알 수 없는 오류'));
      }
    } catch (error) {
      alert('처리 중 오류가 발생했습니다.');
    }
  };

  const isCancellable = (product) => !product.cancelResponseDate;

  const handleToggleCheck = (orderDetailIdx) => {
    setCheckedIdxList((prev) =>
      prev.includes(orderDetailIdx)
        ? prev.filter((idx) => idx !== orderDetailIdx)
        : [...prev, orderDetailIdx]
    );
  };

  const handleCancelSelected = () => {
    if (checkedIdxList.length === 0) {
      alert('취소할 상품을 선택해주세요.');
      return;
    }
    if (!window.confirm(`선택한 ${checkedIdxList.length}개 상품을 취소 처리 하시겠습니까?`)) return;
    setShowReasonModal(true);
  };

  const handleReasonClick = (product) => {
    alert(product.reason || '등록된 취소 사유가 없습니다.');
  };

  const handleReasonModalClose = () => {
    // 사유 입력을 중단하면 취소는 처리되지 않음
    setShowReasonModal(false);
  };

  const handleReasonSubmit = async (requestReason) => {
    try {
      const response = await apiPut('/store/order/status', {
        orderDetailIdxList: checkedIdxList,
        requestReason,
      });
      if (response.ok) {
        alert('취소 처리 되었습니다.');
        setCheckedIdxList([]);
        setShowReasonModal(false);
        await reloadOrderData();
      } else {
        const result = await response.json().catch(() => null);
        alert('취소 처리 실패: ' + (result?.message || '알 수 없는 오류'));
      }
    } catch (error) {
      alert('취소 처리 중 오류가 발생했습니다.');
    }
  };

  const moveReceipt = () => {
    window.open(orderData.receiptUrl);
  };

  if (loading || !orderData) {
    return (
      <div className="orderinfo-wrap" onClick={onClose}>
        <div className="orderinfo-modal" onClick={(e) => e.stopPropagation()}>
          <div className="orderinfo-header">
            <span className="orderinfo-header-title">판매상세</span>
            <button className="orderinfo-header-close" onClick={onClose} aria-label="닫기">✕</button>
          </div>
          <div className="orderinfo-message">
            {loading ? '로딩 중...' : '주문 정보를 불러올 수 없습니다.'}
          </div>
        </div>
      </div>
    );
  }

  const products = orderData.products || [];
  const allCanceled = products.length > 0 && products.every((p) => p.orderStatus === STATUS_CANCEL);
  const payStatus = allCanceled ? '전체 취소' : '결제 완료';

  return (
    <div className="orderinfo-wrap" onClick={onClose}>
      <div className="orderinfo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="orderinfo-header">
          <span className="orderinfo-header-title">판매상세</span>
          <button className="orderinfo-header-close" onClick={onClose} aria-label="닫기">✕</button>
        </div>

        <div className="orderinfo-body">
          {/* 결제 요약 */}
          <div className="orderinfo-summary">
            <div className="orderinfo-summary-total">
              <div className="orderinfo-summary-label">총 결제금액</div>
              <div className="orderinfo-summary-price">
                {orderData.totalPrice != null ? `${orderData.totalPrice.toLocaleString()}원` : '-'}
              </div>
              <span className={`orderinfo-pay-badge${allCanceled ? ' cancel' : ''}`}>
                {payStatus}{orderData.payType ? ` · ${orderData.payType}` : ''}
              </span>
            </div>

            <div className="orderinfo-summary-info">
              <div className="orderinfo-info-item">
                <span className="orderinfo-info-label">판매번호</span>
                <span className="orderinfo-info-value">{orderData.orderCode || '-'}</span>
              </div>
              <div className="orderinfo-info-item">
                <span className="orderinfo-info-label">결제일시</span>
                <span className="orderinfo-info-value">{formatDateTime(orderData.payDate) || '-'}</span>
              </div>
              <div className="orderinfo-info-item orderinfo-info-wide">
                <span className="orderinfo-info-label">결제번호</span>
                <span className="orderinfo-info-value">{orderData.payCode || '-'}</span>
              </div>
            </div>

            <button className="orderinfo-receipt-btn" onClick={moveReceipt}>
              영수증 조회
            </button>
          </div>

          {/* 고객 */}
          <div className="orderinfo-cust">
            <span className="orderinfo-cust-icon"><IoPerson /></span>
            <span className="orderinfo-cust-name">{orderData.custNm || '-'}</span>
            <span className="orderinfo-cust-divider" />
            <span className="orderinfo-cust-sub">{orderData.custTelNo || '-'}</span>
            <span className="orderinfo-cust-divider" />
            <span className="orderinfo-cust-sub muted">{orderData.custId || '-'}</span>
          </div>

          {/* 판매 상품 */}
          <div className="orderinfo-product-head">
            <div className="orderinfo-subtitle">
              판매 상품 <span className="orderinfo-subtitle-count">{products.length}</span>
            </div>
          </div>

          <div className="orderinfo-product-wrap">
            <table className="orderinfo-product-table">
              <thead>
                <tr>
                  <th className="col-check"></th>
                  <th className="col-name">상품명</th>
                  <th className="col-price">수량 · 단가</th>
                  <th className="col-total">결제금액</th>
                  <th className="col-status">상태</th>
                  <th className="col-qr">QR</th>
                  <th className="col-action"></th>
                </tr>
              </thead>

              <tbody>
                {products.length > 0 ? (
                  products.map((product, index) => {
                    const canceled = product.orderStatus === STATUS_CANCEL;
                    return (
                      <tr
                        key={product.orderDetailIdx || index}
                        className={canceled ? 'canceled' : ''}
                      >
                        <td className="col-check">
                          {isCancellable(product) && (
                            <input
                              type="checkbox"
                              disabled={canceled}
                              checked={checkedIdxList.includes(product.orderDetailIdx)}
                              onChange={() => handleToggleCheck(product.orderDetailIdx)}
                            />
                          )}
                        </td>
                        <td className="col-name">{product.productNm || '-'}</td>
                        <td className="col-price">
                          <span className="orderinfo-qty">{product.productAmount || 0} × </span>
                          <span className="orderinfo-price">
                            {product.productPrice ? `${product.productPrice.toLocaleString()}원` : '-'}
                          </span>
                        </td>
                        <td className="col-total">
                          <span className="orderinfo-total">
                            {product.productPrice
                              ? `${(product.productPrice * (product.productAmount || 0)).toLocaleString()}원`
                              : '-'}
                          </span>
                        </td>
                        <td className="col-status">
                          <span className={`orderinfo-status-badge ${statusClass(product.orderStatus)}`}>
                            {product.orderStatus || '-'}
                          </span>
                        </td>
                        <td className="col-qr">{product.qrUsed ? '사용' : '미사용'}</td>
                        <td className="col-action">
                          {product.cancelResponseDate ? (
                            <button
                              className="orderinfo-row-btn"
                              onClick={() => handleReasonClick(product)}
                            >
                              취소 사유
                            </button>
                          ) : !product.qrUsed ? (
                            <button
                              className="orderinfo-row-btn primary"
                              disabled={canceled}
                              onClick={() => handleUseItem(product.orderDetailIdx)}
                            >
                              사용
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="7" className="orderinfo-empty">
                      판매 상품이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="orderinfo-footer">
          <span className="orderinfo-selected-count">
            선택한 상품 {checkedIdxList.length}개
          </span>
          <div className="orderinfo-footer-btns">
            <button className="orderinfo-footer-btn" onClick={handleCancelSelected}>선택 취소</button>
            <button className="orderinfo-footer-btn dark" onClick={onClose}>닫기</button>
          </div>
        </div>
      </div>

      {showReasonModal && (
        <ReasonInputModal
          onClose={handleReasonModalClose}
          onSubmit={handleReasonSubmit}
        />
      )}
    </div>
  );
};

export default OrderInfo;
